import { useSearchParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getListings, ListingItem } from "@/utils/api";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const PAGE_SIZE = 10;

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const mins = Math.floor(diff / (1000 * 60));
      return `${mins}分钟前`;
    }
    return `${hours}小时前`;
  }
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  if (days < 30) return `${Math.floor(days / 7)}周前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

function ListingCard({ listing, index }: { listing: ListingItem; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <Link
        to={`/listing/${listing.id}`}
        className="group flex gap-3 md:gap-4 p-3 md:p-4 bg-white rounded-xl border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:shadow-sm"
      >
        <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg overflow-hidden bg-gray-100">
          {listing.images && listing.images[0] ? (
            <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <i className="ri-image-line text-2xl" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-sm md:text-base font-semibold text-gray-800 group-hover:text-orange-600 transition-colors line-clamp-1">
              {listing.is_pinned && (
                <span className="inline-flex items-center px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded mr-1.5">置顶</span>
              )}
              {listing.title}
            </h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {listing.category && (
                <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full">{listing.category.name}</span>
              )}
              <span className="text-gray-400 text-xs">{formatDate(listing.created_at)}</span>
              <span className="text-gray-400 text-xs flex items-center gap-0.5">
                <i className="ri-eye-line" />{listing.view_count}
              </span>
            </div>
            <p className="text-gray-500 text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed">{listing.content}</p>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              {listing.user && (
                <>
                  <img
                    src={listing.user.avatar || "/default-avatar.png"}
                    alt={listing.user.nickname}
                    className="w-5 h-5 rounded-full object-cover bg-gray-100"
                  />
                  <span className="text-xs text-gray-500">{listing.user.nickname}</span>
                </>
              )}
            </div>
            <button
              onClick={(e) => toggleFavorite(listing.id, e)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <i className={`${favorites.has(listing.id) ? "ri-heart-fill text-orange-500" : "ri-heart-line text-gray-400"} text-base`} />
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}

function Pagination({ current, total, onChange }: { current: number; total: number; onChange: (p: number) => void }) {
  if (total <= 1) return null;
  const pages = Array.from({ length: total }, (_, i) => i + 1);
  return (
    <div className="flex items-center justify-center gap-1.5 mt-8">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <i className="ri-arrow-left-s-line" />
      </button>
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
            p === current
              ? "bg-orange-500 text-white border border-orange-500"
              : "border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500"
          }`}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <i className="ri-arrow-right-s-line" />
      </button>
    </div>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(query);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 获取数据
  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getListings({
          q: query,
          page: currentPage,
          page_size: PAGE_SIZE,
        });
        setListings(res.list || []);
        setTotal(res.total || 0);
      } catch (err) {
        console.error('搜索失败:', err);
        setError('加载数据失败，请稍后重试');
        setListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [query, currentPage]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
      setCurrentPage(1);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="pt-20">
        {/* Search Header */}
        <div className="bg-white border-b border-gray-100">
          <div className="w-full px-4 md:px-8 py-6 md:py-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
                {query ? `搜索结果: "${query}"` : "搜索服务"}
              </h1>
              <form onSubmit={handleSearch}>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="请输入关键词"
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors text-sm whitespace-nowrap"
                  >
                    搜索
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-gray-500">加载中...</span>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
                  <i className="ri-error-warning-line text-2xl text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">{error}</h3>
                <button
                  onClick={() => setCurrentPage(1)}
                  className="mt-4 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
                >
                  重新加载
                </button>
              </div>
            )}

            {/* Results List */}
            {!loading && !error && listings.length > 0 && (
              <>
                <div className="text-sm text-gray-500 mb-4">
                  共找到 <span className="text-orange-500 font-medium">{total}</span> 条相关服务
                </div>
                <div className="space-y-3 md:space-y-4">
                  {listings.map((listing, index) => (
                    <ListingCard key={listing.id} listing={listing} index={index} />
                  ))}
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
              </>
            )}

            {/* Empty State */}
            {!loading && !error && listings.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
                  <i className="ri-search-line text-2xl text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">未找到相关结果</h3>
                <p className="text-gray-500 text-sm">
                  {query ? "试试其他关键词，或浏览分类查找服务" : "请输入关键词搜索服务"}
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center mt-4 px-5 py-2.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors text-sm"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
